import { useState, useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import useFetchList from './useFetchList';
import type { Resource } from '../api/endpoints';
import type { QueryParams } from '../api/client';
import type { Notify } from './useNotification';

// The create / update / delete cycle every module reimplements:
// build payload -> POST or PUT -> splice the result into local state ->
// show a notification -> reset the form -> go back to the list view.
//
//   const crud = useCrud(endpoints.events, { notify, label: 'Event' });
//   crud.items, crud.loading, crud.save(formData), crud.remove(id), crud.startEdit(row)

export interface UseCrudOptions {
  notify?: Notify;
  /** Used in the success/error messages, e.g. 'Event updated successfully'. */
  label?: string;
  params?: QueryParams;
  /** Primary-key field. Defaults to `id`. */
  idKey?: string;
}

export interface UseCrudResult<T> {
  items: T[];
  setItems: Dispatch<SetStateAction<T[]>>;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  saving: boolean;
  isEditing: boolean;
  editingId: number | string | null;
  startEdit: (row: T) => void;
  cancelEdit: () => void;
  /** Resolves true on success, false if the request failed. */
  save: (payload: Partial<T>) => Promise<boolean>;
  remove: (id: number | string) => Promise<boolean>;
}

export const useCrud = <T = unknown>(
  resource: Resource<T>,
  { notify, label = 'Record', params, idKey = 'id' }: UseCrudOptions = {}
): UseCrudResult<T> => {
  const { data: items, setData: setItems, loading, error, refetch } = useFetchList(
    (p, options) => resource.list(p, options),
    { params }
  );

  const [saving, setSaving] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const isEditing = editingId !== null;

  const announce = useCallback(
    (message: string, type?: 'success' | 'error') => {
      if (typeof notify === 'function') notify(message, type);
    },
    [notify]
  );

  const startEdit = useCallback(
    (row: T) => setEditingId((row as Record<string, never>)?.[idKey] ?? null),
    [idKey]
  );
  const cancelEdit = useCallback(() => setEditingId(null), []);

  const save = useCallback(
    async (payload: Partial<T>): Promise<boolean> => {
      setSaving(true);
      try {
        if (isEditing) {
        // NOTE: when the server responds with an empty body we fall back to
        // the payload we just sent. That row is only as complete as what the
        // client knew — any server-assigned or server-normalised field is
        // missing until the next refetch. The cast records that assumption
        // rather than hiding it. (Same issue as the Donations receipt preview.)
          // The server replaces the whole record, so the row it holds now is
          // sent back with the form laid over it — otherwise every column the
          // form does not show (createdBy above all) would be blanked.
          const existing = items.find((it) => (it as Record<string, unknown>)[idKey] === editingId) || {};
          const body = { ...existing, ...payload, [idKey]: editingId } as Partial<T>;
          const updated = await resource.update(editingId, body);
          // Held for approval: the server sends the record as it still is.
          const held = Boolean((updated as { approvalPending?: boolean } | null)?.approvalPending);
          const merged = (updated || body) as T;
          setItems((prev) =>
            prev.map((it) => ((it as Record<string, unknown>)[idKey] === editingId ? merged : it))
          );
          announce(held ? `${label} change sent for approval — it applies when approved` : `${label} updated successfully`, 'success');
        } else {
          const created = await resource.create(payload);
          if ((created as { approvalPending?: boolean } | null)?.approvalPending) {
            // Held for approval (Settings › Approvals): nothing was saved yet,
            // so it does not belong in the list. The app shows the notice.
            announce(`${label} sent for approval — it is added when approved`, 'success');
          } else {
            // See the note above: `payload` is a Partial<T> standing in for the
            // saved record when the server returns nothing.
            setItems((prev) => [(created || payload) as T, ...prev]);
            announce(`${label} saved successfully`, 'success');
          }
        }
        setEditingId(null);
        return true;
      } catch (err) {
        announce(err.message || `Could not save ${label.toLowerCase()}.`, 'error');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [resource, isEditing, editingId, idKey, items, setItems, announce, label]
  );

  const remove = useCallback(
    async (id: number | string): Promise<boolean> => {
      setSaving(true);
      try {
        await resource.remove(id);
        setItems((prev) => prev.filter((it) => it[idKey] !== id));
        announce(`${label} deleted`, 'success');
        return true;
      } catch (err) {
        announce(err.message || `Could not delete ${label.toLowerCase()}.`, 'error');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [resource, idKey, setItems, announce, label]
  );

  return {
    items, setItems, loading, error, refetch,
    saving, isEditing, editingId, startEdit, cancelEdit, save, remove
  };
};

export default useCrud;
